import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { Observable } from 'rxjs';
import { CeDialogService, ModalSizeOption, StaticDialogConfig } from '@kempower-nx-workspace/shared/kp-design-system';
import { FormGroup } from '@angular/forms';
import { inject } from '@angular/core';

export interface RouteGuardForm {
    form: FormGroup;
    originalData?: object;
}
export interface FormRouteGuards {
    routeGuardForms(): RouteGuardForm[];
}

export interface HasUnsavedChanges {
    form: FormGroup;
    originalData: object;
    router: Router;
    routeGuardForms?(): RouteGuardForm[];
}

export class FormUnsavedChangesGuard {
    readonly #router = inject(Router);
    readonly #dialogService = inject(CeDialogService);
    allowNavigation = false;

    canDeactivate(component: HasUnsavedChanges, _currentRoute: ActivatedRouteSnapshot, _currentState: RouterStateSnapshot, nextState: RouterStateSnapshot): Observable<boolean> | boolean {
        // check registered forms for routeguard
        let hasDirtyForm = false;
        /**
         * First check if the component has any forms registered to be checked by the route guard
         * Check if any are dirty, if so show Dialog.
         */
        if (component?.routeGuardForms && typeof component.routeGuardForms === 'function') {
            const pristine = component.routeGuardForms().filter((f) => !f.form.pristine);
            hasDirtyForm = pristine.length > 0;
        } else if (component?.form) {
            // Legacy approach: using a global `form` property to check for route guard logic.
            // Recommended: register forms directly within the components
            // that need forms to be checked on route change.
            // This ensures a clear relationship between the component and this associated route guard.
            hasDirtyForm = !component?.form.pristine;
        }

        if (!hasDirtyForm) {
            return true;
        }

        if (hasDirtyForm) {
            const config: StaticDialogConfig = {
                size: ModalSizeOption.MEDIUM,
                title: 'Discard unsaved changes?',
                dialogContentText: 'You have unsaved changes on this page. Leaving the page will discard those changes.',
                primaryBtnText: 'Discard and leave',
                secondaryBtnText: 'Keep editing',
                compactContent: true,
                callbackFunction: (confirm: boolean): void => {
                    if (confirm) {
                        this.allowNavigation = true;
                        if (component?.routeGuardForms) {
                            component.routeGuardForms().forEach(({ form, originalData }) => {
                                if (originalData) {
                                    form.patchValue(originalData); // Optional: reset form values
                                }
                                form.markAsPristine();
                                form.updateValueAndValidity(); // Optional: ensures Angular status is up to date
                            });
                        }

                        if (component?.form) {
                            component.form.markAsPristine();
                            component.form.patchValue(component.originalData);
                        }
                        this.#router.navigate([nextState.url]);
                    }
                    // Revert to original value, so editing a different form gets caught.
                    this.allowNavigation = false;
                },
            };

            this.#dialogService.openStaticDialog(config);
        }
        return this.allowNavigation;
    }
}
